import type { MemberShowcaseFunctionSlug } from "@/lib/member-showcase";
import { MEMBER_SHOWCASE_FUNCTIONS } from "@/lib/member-showcase";

const primary = "hsl(var(--primary))";
const secondary = "hsl(var(--secondary))";
const mutedFill = "hsl(var(--foreground) / 0.14)";
const strokeW = 2;

type IllustrationProps = {
  className?: string;
  ariaLabel: string;
};

function SimplePerson({
  x,
  y,
  color = primary,
  scale = 1,
}: {
  x: number;
  y: number;
  color?: string;
  scale?: number;
}) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`} aria-hidden="true">
      <circle cx={12} cy={8} r={7} stroke={color} strokeWidth={strokeW} />
      <rect
        x={4}
        y={16}
        width={16}
        height={18}
        rx={6}
        stroke={color}
        strokeWidth={strokeW}
      />
    </g>
  );
}

/** Curved question mark shape without SVG text */
function QuestionNotch({ x, y, color = primary }: { x: number; y: number; color?: string }) {
  return (
    <g transform={`translate(${x},${y})`} aria-hidden="true">
      <path
        d="M6 2 C6 0 10 0 10 4 C10 7 6 7 6 10"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx={6} cy={14} r={1.5} fill={color} />
    </g>
  );
}

function TalentAcquisitionIllustration({ className, ariaLabel }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        <rect x={12} y={48} width={52} height={36} rx={6} fill={mutedFill} stroke={primary} strokeWidth={strokeW} transform="rotate(-6 38 66)" />
        <rect x={20} y={56} width={52} height={36} rx={6} fill={mutedFill} stroke={primary} strokeWidth={strokeW} transform="rotate(4 46 74)" />
        <rect x={8} y={64} width={52} height={36} rx={6} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} transform="rotate(-3 34 82)" />
        <line x1={18} y1={72} x2={48} y2={72} stroke={primary} strokeWidth={1.5} opacity={0.5} />
        <line x1={18} y1={80} x2={40} y2={80} stroke={primary} strokeWidth={1.5} opacity={0.5} />

        <rect x={118} y={40} width={76} height={100} rx={10} fill="hsl(var(--secondary) / 0.12)" stroke={secondary} strokeWidth={strokeW} />
        <rect x={130} y={54} width={52} height={8} rx={4} fill={secondary} opacity={0.35} />
        <line x1={130} y1={72} x2={182} y2={72} stroke={secondary} strokeWidth={1.5} opacity={0.5} />
        <line x1={130} y1={82} x2={170} y2={82} stroke={secondary} strokeWidth={1.5} opacity={0.5} />
        <line x1={130} y1={92} x2={176} y2={92} stroke={secondary} strokeWidth={1.5} opacity={0.5} />
        <circle cx={154} cy={118} r={10} fill={secondary} opacity={0.2} stroke={secondary} strokeWidth={strokeW} />
        <path d="M150 118 L154 122 L162 112" stroke={secondary} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        <rect x={228} y={52} width={56} height={44} rx={8} fill={mutedFill} stroke={primary} strokeWidth={strokeW} />
        <circle cx={252} cy={68} r={8} stroke={primary} strokeWidth={strokeW} />
        <rect x={244} y={78} width={16} height={12} rx={5} stroke={primary} strokeWidth={strokeW} />

        <rect x={248} y={72} width={56} height={44} rx={8} fill="hsl(var(--primary) / 0.12)" stroke={primary} strokeWidth={strokeW} />
        <circle cx={276} cy={88} r={8} stroke={primary} strokeWidth={strokeW} />
        <rect x={268} y={98} width={16} height={12} rx={5} stroke={primary} strokeWidth={strokeW} />

        <rect x={236} y={92} width={56} height={44} rx={8} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} />
        <circle cx={260} cy={108} r={8} stroke={secondary} strokeWidth={strokeW} />
        <rect x={252} y={118} width={16} height={12} rx={5} stroke={secondary} strokeWidth={strokeW} />
      </g>
    </svg>
  );
}

function TalentManagementLearningIllustration({ className, ariaLabel }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        <path d="M48 170 L48 110 L100 70" stroke={primary} strokeWidth={strokeW} strokeLinecap="round" />
        <path d="M48 110 L100 130" stroke={secondary} strokeWidth={strokeW} strokeLinecap="round" />
        <circle cx={48} cy={170} r={6} fill={primary} opacity={0.3} stroke={primary} strokeWidth={strokeW} />
        <circle cx={100} cy={70} r={6} fill={secondary} opacity={0.3} stroke={secondary} strokeWidth={strokeW} />
        <circle cx={100} cy={130} r={6} fill={primary} opacity={0.3} stroke={primary} strokeWidth={strokeW} />

        <rect x={130} y={36} width={72} height={90} rx={8} fill={mutedFill} stroke={primary} strokeWidth={strokeW} />
        <line x1={142} y1={54} x2={190} y2={54} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={142} y1={66} x2={182} y2={70} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={142} y1={78} x2={188} y2={74} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={142} y1={90} x2={176} y2={94} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={142} y1={102} x2={184} y2={98} stroke={primary} strokeWidth={1.5} opacity={0.4} />

        <rect x={210} y={48} width={72} height={90} rx={8} fill="hsl(var(--secondary) / 0.12)" stroke={secondary} strokeWidth={strokeW} />
        <line x1={222} y1={66} x2={270} y2={66} stroke={secondary} strokeWidth={2} opacity={0.6} />
        <line x1={222} y1={80} x2={270} y2={80} stroke={secondary} strokeWidth={2} opacity={0.6} />
        <line x1={222} y1={94} x2={258} y2={94} stroke={secondary} strokeWidth={2} opacity={0.6} />
        <rect x={222} y={108} width={48} height={10} rx={4} fill={secondary} opacity={0.25} />
        <line x1={202} y1={90} x2={210} y2={90} stroke={secondary} strokeWidth={strokeW} strokeLinecap="round" />
        <polygon points="206,86 214,90 206,94" fill={secondary} />
      </g>
    </svg>
  );
}

function PeopleOperationsIllustration({ className, ariaLabel }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        <rect x={24} y={72} width={64} height={48} rx={6} fill={mutedFill} stroke={primary} strokeWidth={strokeW} transform="rotate(-8 56 96)" />
        <rect x={32} y={64} width={64} height={48} rx={6} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} transform="rotate(5 64 88)" />
        <rect x={20} y={80} width={64} height={48} rx={6} fill={mutedFill} stroke={primary} strokeWidth={strokeW} />

        <circle cx={148} cy={88} r={28} stroke={secondary} strokeWidth={strokeW} fill="hsl(var(--secondary) / 0.08)" />
        <line x1={168} y1={108} x2={188} y2={128} stroke={secondary} strokeWidth={strokeW} strokeLinecap="round" />
        <rect x={132} y={78} width={32} height={24} rx={4} fill={mutedFill} stroke={primary} strokeWidth={1.5} />
        <line x1={138} y1={86} x2={162} y2={86} stroke={primary} strokeWidth={1.5} opacity={0.5} />
        <QuestionNotch x={142} y={88} />

        <rect x={220} y={56} width={72} height={96} rx={8} fill="hsl(var(--primary) / 0.1)" stroke={primary} strokeWidth={strokeW} />
        <line x1={234} y1={76} x2={298} y2={76} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={234} y1={90} x2={286} y2={90} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <rect x={234} y={104} width={20} height={20} rx={4} fill={primary} opacity={0.2} stroke={primary} strokeWidth={strokeW} />
        <path d="M238 114 L244 120 L252 108" stroke={primary} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <line x1={260} y1={114} x2={290} y2={114} stroke={primary} strokeWidth={1.5} opacity={0.4} />
      </g>
    </svg>
  );
}

function PeopleAnalyticsIllustration({ className, ariaLabel }: IllustrationProps) {
  const barHeights = [48, 72, 36, 64, 52];
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        {barHeights.map((h, col) => {
          const x = 40 + col * 28;
          const isHighlight = col === 2;
          return (
            <rect
              key={col}
              x={x}
              y={140 - h}
              width={20}
              height={h}
              rx={4}
              fill={isHighlight ? "hsl(var(--primary) / 0.2)" : mutedFill}
              stroke={isHighlight ? primary : secondary}
              strokeWidth={strokeW}
              opacity={isHighlight ? 1 : 0.7}
            />
          );
        })}
        <line x1={32} y1={148} x2={180} y2={148} stroke={secondary} strokeWidth={1.5} opacity={0.4} />
        <rect x={96} y={88} width={20} height={14} rx={3} fill="hsl(var(--background))" stroke={primary} strokeWidth={strokeW} />
        <QuestionNotch x={100} y={90} />

        <rect x={200} y={44} width={88} height={112} rx={10} fill="hsl(var(--secondary) / 0.1)" stroke={secondary} strokeWidth={strokeW} />
        {[0, 1, 2, 3].map((row) => (
          <line
            key={row}
            x1={214}
            y1={64 + row * 22}
            x2={274}
            y2={64 + row * 22}
            stroke={secondary}
            strokeWidth={1.5}
            opacity={0.45}
          />
        ))}
        <rect x={214} y={120} width={56} height={10} rx={4} fill={secondary} opacity={0.25} />
      </g>
    </svg>
  );
}

function WorkforcePlanningIllustration({ className, ariaLabel }: IllustrationProps) {
  const headcountDots = [
    { cx: 28, cy: 58 },
    { cx: 44, cy: 58 },
    { cx: 60, cy: 58 },
    { cx: 36, cy: 74 },
    { cx: 52, cy: 74 },
    { cx: 44, cy: 90 },
  ];
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        <rect x={16} y={44} width={56} height={36} rx={6} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} />
        <line x1={44} y1={80} x2={44} y2={96} stroke={secondary} strokeWidth={strokeW} strokeLinecap="round" />
        <rect x={8} y={96} width={40} height={28} rx={5} fill={mutedFill} stroke={primary} strokeWidth={strokeW} />
        <rect x={40} y={96} width={40} height={28} rx={5} fill="hsl(var(--primary) / 0.1)" stroke={primary} strokeWidth={strokeW} />
        {headcountDots.map((dot, i) => (
          <circle
            key={i}
            cx={dot.cx}
            cy={dot.cy}
            r={4}
            fill={i % 2 === 0 ? primary : secondary}
            opacity={0.35}
          />
        ))}

        <rect x={118} y={48} width={76} height={100} rx={10} fill="hsl(var(--secondary) / 0.1)" stroke={secondary} strokeWidth={strokeW} />
        <line x1={130} y1={66} x2={182} y2={66} stroke={secondary} strokeWidth={1.5} opacity={0.45} />
        <line x1={130} y1={80} x2={176} y2={80} stroke={secondary} strokeWidth={1.5} opacity={0.45} />
        <line x1={130} y1={94} x2={170} y2={94} stroke={secondary} strokeWidth={1.5} opacity={0.45} />
        <line x1={130} y1={108} x2={178} y2={108} stroke={secondary} strokeWidth={1.5} opacity={0.45} />
        <rect x={130} y={122} width={48} height={10} rx={4} fill={secondary} opacity={0.25} />

        <SimplePerson x={220} y={56} color={primary} scale={1.3} />
        <SimplePerson x={268} y={72} color={secondary} scale={1.2} />
        <rect x={212} y={128} width={88} height={36} rx={8} fill={mutedFill} stroke={primary} strokeWidth={strokeW} />
        <line x1={224} y1={142} x2={288} y2={142} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={224} y1={154} x2={272} y2={154} stroke={primary} strokeWidth={1.5} opacity={0.4} />
      </g>
    </svg>
  );
}

function HrBusinessPartnerIllustration({ className, ariaLabel }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <g aria-hidden="true">
        <rect x={32} y={48} width={64} height={100} rx={8} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} />
        <rect x={44} y={60} width={40} height={32} rx={4} fill={secondary} opacity={0.25} />
        <line x1={44} y1={104} x2={84} y2={104} stroke={secondary} strokeWidth={1.5} opacity={0.4} />
        <line x1={44} y1={116} x2={76} y2={116} stroke={secondary} strokeWidth={1.5} opacity={0.4} />

        <rect x={108} y={48} width={64} height={100} rx={8} fill="hsl(var(--primary) / 0.1)" stroke={primary} strokeWidth={strokeW} />
        <rect x={120} y={60} width={40} height={56} rx={4} fill={primary} opacity={0.25} />
        <line x1={120} y1={104} x2={160} y2={104} stroke={primary} strokeWidth={1.5} opacity={0.4} />
        <line x1={120} y1={116} x2={152} y2={116} stroke={primary} strokeWidth={1.5} opacity={0.4} />

        <line x1={96} y1={98} x2={108} y2={98} stroke={primary} strokeWidth={strokeW} strokeLinecap="round" />
        <polygon points="96,94 88,98 96,102" fill={primary} />
        <polygon points="108,94 116,98 108,102" fill={primary} />

        <SimplePerson x={228} y={64} color={primary} scale={1.4} />
        <rect x={216} y={118} width={72} height={40} rx={8} fill={mutedFill} stroke={secondary} strokeWidth={strokeW} />
        <line x1={228} y1={132} x2={300} y2={132} stroke={secondary} strokeWidth={1.5} opacity={0.4} />
        <line x1={228} y1={144} x2={288} y2={144} stroke={secondary} strokeWidth={1.5} opacity={0.4} />
      </g>
    </svg>
  );
}

const illustrations: Record<
  MemberShowcaseFunctionSlug,
  (props: IllustrationProps) => React.ReactElement
> = {
  "talent-acquisition": TalentAcquisitionIllustration,
  "talent-management-learning": TalentManagementLearningIllustration,
  "people-operations": PeopleOperationsIllustration,
  "people-analytics": PeopleAnalyticsIllustration,
  "workforce-planning": WorkforcePlanningIllustration,
  "hr-business-partner": HrBusinessPartnerIllustration,
};

const titleBySlug = Object.fromEntries(
  MEMBER_SHOWCASE_FUNCTIONS.map((fn) => [fn.slug, fn.title]),
) as Record<MemberShowcaseFunctionSlug, string>;

export function FunctionIllustration({
  slug,
  className,
}: {
  slug: MemberShowcaseFunctionSlug;
  className?: string;
}) {
  const Illustration = illustrations[slug];
  return (
    <Illustration
      className={className}
      ariaLabel={titleBySlug[slug]}
    />
  );
}
